/**
 * ContactMessagesPanel - Painel de mensagens de contato para admin
 */
import React, { useState, useEffect, useCallback } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Mail, MailOpen, Clock, CheckCircle, ArrowLeft, Trash2 } from "lucide-react";
import { useTranslation } from "../translations";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactMessagesPanel = () => {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/contact-messages`);
      setMessages(res.data.messages || []);
      setUnread(res.data.unread || 0);
    } catch {
      // not admin or error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const markRead = async (id) => {
    await axios.put(`${API}/admin/contact-messages/${id}/read`);
    fetchMessages();
  };

  const [deletingId, setDeletingId] = useState(null);

  const deleteMessage = async (id) => {
    if (deletingId === id) {
      // Second tap = confirm delete
      try {
        await axios.delete(`${API}/admin/contact-messages/${id}`);
        setSelected(null);
        setDeletingId(null);
        fetchMessages();
      } catch {
        setDeletingId(null);
      }
    } else {
      // First tap = highlight for confirmation
      setDeletingId(id);
      // Auto-cancel after 3 seconds
      setTimeout(() => setDeletingId(null), 3000);
    }
  };

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/contact-messages/${id}/status?status=${status}`);
    fetchMessages();
  };

  const statusBadge = (status) => {
    const map = {
      new: { label: t('msgNew') || "Nova", cls: "bg-blue-100 text-blue-800" },
      read: { label: t('msgRead') || "Lida", cls: "bg-gray-100 text-gray-800" },
      responded: { label: t('msgResponded') || "Respondida", cls: "bg-green-100 text-green-800" }
    };
    const s = map[status] || map.new;
    return <Badge className={s.cls}>{s.label}</Badge>;
  };

  if (loading) return <div className="text-center py-8 text-gray-500">{t('loading') || 'Carregando...'}</div>;

  // Detalhe da mensagem
  if (selected) {
    return (
      <Card className="p-6" data-testid="message-detail">
        <Button variant="ghost" size="sm" className="mb-4" onClick={() => setSelected(null)} data-testid="message-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> {t('back') || 'Voltar'}
        </Button>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold">{selected.subject}</h3>
            {statusBadge(selected.status)}
          </div>
          <div className="flex gap-4 text-sm text-gray-500">
            <span>{selected.name}</span>
            <span>{selected.email}</span>
            <span>{new Date(selected.created_at).toLocaleString('pt-BR')}</span>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-gray-800 whitespace-pre-wrap">
            {selected.message}
          </div>
          <div className="flex gap-2 flex-wrap">
            {selected.status !== "responded" && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => { updateStatus(selected.id, "responded"); setSelected({...selected, status: "responded"}); }} data-testid="mark-responded">
                <CheckCircle className="w-4 h-4 mr-1" /> {t('msgMarkResponded') || 'Marcar como Respondida'}
              </Button>
            )}
            <a href={`mailto:${selected.email}?subject=Re: ${selected.subject}`}>
              <Button size="sm" variant="outline" data-testid="reply-email">
                <Mail className="w-4 h-4 mr-1" /> {t('msgReplyEmail') || 'Responder por Email'}
              </Button>
            </a>
            <Button 
              size="sm" 
              variant={deletingId === selected.id ? "destructive" : "destructive"}
              className={`touch-manipulation ${deletingId === selected.id ? "animate-pulse" : ""}`}
              onClick={() => deleteMessage(selected.id)} 
              data-testid="delete-message"
            >
              <Trash2 className="w-4 h-4 mr-1" /> 
              {deletingId === selected.id 
                ? (t('msgDeleteConfirm') || 'Confirmar Exclusão') 
                : (t('msgDelete') || 'Excluir')
              }
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Lista de mensagens
  return (
    <div data-testid="messages-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Mail className="w-5 h-5" />
          {t('msgTitle') || 'Mensagens de Contato'}
          {unread > 0 && <Badge className="bg-red-500 text-white">{unread}</Badge>}
        </h2>
      </div>

      {messages.length === 0 ? (
        <Card className="p-8 text-center text-gray-500">
          <MailOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>{t('msgEmpty') || 'Nenhuma mensagem recebida ainda.'}</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {messages.map((msg) => (
            <Card
              key={msg.id}
              className={`p-4 hover:shadow-md transition-shadow ${!msg.read ? 'border-l-4 border-l-blue-500' : ''}`}
              data-testid={`message-item-${msg.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div 
                  className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                  onClick={() => { if (!msg.read) markRead(msg.id); setSelected(msg); }}
                >
                  {!msg.read ? <Mail className="w-4 h-4 text-blue-500 flex-shrink-0" /> : <MailOpen className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className={`text-sm truncate ${!msg.read ? 'font-bold text-gray-900' : 'text-gray-600'}`}>
                      {msg.subject}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{msg.name} - {msg.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {statusBadge(msg.status)}
                  <span className="text-xs text-gray-400 flex items-center gap-1 hidden sm:flex">
                    <Clock className="w-3 h-3" />
                    {new Date(msg.created_at).toLocaleDateString('pt-BR')}
                  </span>
                  <Button 
                    size="sm" 
                    variant={deletingId === msg.id ? "destructive" : "ghost"}
                    className={`h-10 w-10 min-w-[40px] p-0 touch-manipulation ${
                      deletingId === msg.id 
                        ? "bg-red-500 text-white animate-pulse" 
                        : "text-red-500 hover:text-red-700 hover:bg-red-50"
                    }`}
                    onClick={(e) => { e.stopPropagation(); deleteMessage(msg.id); }}
                    data-testid={`delete-message-${msg.id}`}
                    title={deletingId === msg.id ? (t('msgDeleteConfirm') || 'Toque novamente para excluir') : (t('msgDelete') || 'Excluir')}
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactMessagesPanel;
